import { zodResolver } from "@hookform/resolvers/zod";
import { Help, PriorityHigh } from "@mui/icons-material";
import { Alert, Box, Stack, Typography } from "@mui/joy";
import { useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { ApiCompetitionDetail } from "griptonite-api-client";
import { useEffect, useMemo } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";

import {
  useCompetitionQuery,
  useCreateCompetitionMutation,
  useUpdateCompetitionMutation,
} from "@/hooks/competitions";
import { useCurrentVenueQuery } from "@/hooks/venues";
import { handleFormError } from "@/utils/handleFormError";

import FormErrorAlert from "../FormErrorAlert";
import { MediaSelectInput } from "../forms/MediaInput";
import { TagScanInput } from "../forms/TagInput";
import TextInput, { DateInput, TextareaInput } from "../forms/TextInput";
import { ToggleButtonSelectInput } from "../forms/ToggleButtonSelect";
import VenueSelectInput from "../forms/VenueSelectInput";
import BottomButtons from "./BottomButtons";
import {
  competitionToDetailsFormValues,
  DetailsFormSchema,
  DetailsFormValues,
  detailsFormValuesToPayload,
} from "./forms";
import PublishWarningsAlert from "./PublishWarningsAlert";
import SponsorSelectInput from "./SponsorSelectInput";

const CompetitionDetailsForm = ({
  competitionUuid,
  startDate,
  leagueUuid,
}: {
  competitionUuid?: string;
  startDate?: string;
  leagueUuid?: string;
}) => {
  const isCreating = !competitionUuid;

  const { data: venue } = useCurrentVenueQuery();

  const {
    data: competitionSummaryOrDetail,
    isFetching,
    isPending,
  } = useCompetitionQuery(competitionUuid);

  const detailPending =
    (!isCreating && isPending) || (!competitionSummaryOrDetail?.settings && isFetching);

  const competition = competitionSummaryOrDetail as ApiCompetitionDetail | undefined;

  const isInLeague = !!competition?.league || !!leagueUuid;
  const canSelectVenue = isInLeague && (competition?.entriesCount || 0) === 0;

  const defaultValues = useMemo(
    () => competitionToDetailsFormValues(competition, venue, startDate, leagueUuid),
    [competition, venue, startDate, leagueUuid],
  );

  const methods = useForm({
    mode: "onBlur",
    resolver: zodResolver(DetailsFormSchema),
    defaultValues,
  });

  useEffect(() => {
    methods.reset(defaultValues);
  }, [defaultValues, methods]);

  const createMutation = useCreateCompetitionMutation();
  const updateMutation = useUpdateCompetitionMutation(
    competition?.uuid,
    venue?.uuid,
    competition?.league?.uuid,
  );
  const navigate = useNavigate();

  if (detailPending) {
    return null;
  }

  const onSubmit = async (values: DetailsFormValues) => {
    const payload = detailsFormValuesToPayload(values);
    if (isCreating) {
      const competition = await createMutation.mutateAsync(
        { ...payload, autoRouteGroupMode: "SINGLE" },
        {
          onError: async (error) => {
            handleFormError(error, methods);
          },
        },
      );
      navigate({
        to: "/gym/$venueUuid/events/$competitionUuid/profile/categories",
        params: {
          venueUuid: values.venue,
          competitionUuid: competition.uuid,
        },
        replace: true,
      });
    } else {
      const competition = await updateMutation.mutateAsync(payload, {
        onError: async (error) => {
          handleFormError(error, methods);
        },
      });
      if (competition.isHidden) {
        navigate({
          to: "/gym/$venueUuid/events/$competitionUuid/profile/categories",
          params: {
            venueUuid: values.venue,
            competitionUuid: competition.uuid,
          },
          replace: true,
        });
      }
    }
  };

  return (
    <Stack
      sx={{ padding: 1.5, gap: 1, flex: 1, overflowY: "auto" }}
      component="form"
      autoComplete="off"
      onSubmit={methods.handleSubmit(onSubmit as (vals: typeof defaultValues) => Promise<void>)}
    >
      <FormProvider {...methods}>
        <FormErrorAlert />
        <PublishWarningsAlert tab="details" competition={competition} />
        <FormFields competition={competition} displayVenueInput={canSelectVenue} />
        <BottomButtons
          formState={methods.formState}
          nextLink={
            competition
              ? {
                  to: "/gym/$venueUuid/events/$competitionUuid/profile/categories",
                  params: {
                    venueUuid: venue?.uuid,
                    competitionUuid: competition.uuid,
                  },
                  replace: true,
                }
              : undefined
          }
          competition={competition}
        />
      </FormProvider>
    </Stack>
  );
};

const FormFields = ({
  competition,
  displayVenueInput,
}: {
  competition?: ApiCompetitionDetail;
  displayVenueInput?: boolean;
}) => {
  const { watch } = useFormContext();
  const tagEntry = watch("tagEntry");
  const tvMode = watch("tvMode");
  const startTime = watch("startTime");

  const didVenueChange = competition && watch("venue") !== competition?.venues[0]?.uuid;

  return (
    <>
      {displayVenueInput ? (
        <>
          <VenueSelectInput name="venue" label="Gym" />
          {didVenueChange ? (
            <Alert
              startDecorator={<PriorityHigh />}
              sx={{ alignItems: "flex-start" }}
              color="danger"
            >
              <Typography level="body-sm" fontWeight="bold">
                Changing the gym will remove any set routes from the competition. Only proceed if
                you are sure this is OK!
              </Typography>
            </Alert>
          ) : null}
        </>
      ) : null}
      <TextInput name="name" label="Name" />
      <Stack direction="row" spacing={2}>
        <Box sx={{ flex: 1 }}>
          <DateInput
            name="startTime"
            label="Starts at"
            {...(competition === undefined ? { minDate: "TODAY" } : {})}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <DateInput
            name="endTime"
            label="Ends at"
            minDate={startTime && format(startTime, "yyyy-MM-dd")}
          />
        </Box>
      </Stack>
      <ToggleButtonSelectInput
        name="climbType"
        label="Climb type"
        options={[
          {
            value: "BOULDER",
            name: "Bouldering",
          },
          {
            value: "SPORT",
            name: "Sport",
          },
        ]}
        sx={{ ".MuiButton-root": { flex: 1 } }}
        disabled={!!competition}
      />
      <TextareaInput name="description" label="Description" />
      <TextInput name="cost" label="Cost" />
      <MediaSelectInput name="media" label="Media" multiple accept="image/*,video/*" />
      <Box>
        <Typography fontSize={15} marginBottom={1} fontWeight="bold">
          Links
        </Typography>
        <Stack gap={1} sx={{ label: { fontSize: 13 } }}>
          <TextInput name="websiteUrl" label="Website" />
          <TextInput name="facebookUrl" label="Facebook" />
          <TextInput name="instagramUrl" label="Instagram" />
        </Stack>
      </Box>
      <SponsorSelectInput name="sponsorItems" label="Sponsors" />
      <Stack gap={1}>
        <ToggleButtonSelectInput
          name="tagEntry"
          options={[
            { value: true, name: "Yes" },
            { value: false, name: "No" },
          ]}
          label="Entry tag"
          sx={{
            ".MuiButton-root": { flex: 1 },
          }}
        />
        {tagEntry && <TagScanInput name="tagId" />}
        <Alert startDecorator={<Help />} sx={{ alignItems: "flex-start" }}>
          <div>
            <Typography level="body-sm" fontWeight="bold" textColor="neutral.700">
              What is an entry tag?
            </Typography>
            <Typography level="body-sm" color="neutral">
              This option allows you to require everyone who wants to enter the competition to scan
              a Griptag. You can keep this Griptag at reception and get customers to scan it on
              entry.
            </Typography>
          </div>
        </Alert>
        <Stack>
          <ToggleButtonSelectInput
            name="tvDedicatedMode"
            label="TV mode"
            options={[
              { value: false, name: "Tab" },
              { value: true, name: "Dedicated" },
            ]}
            sx={{
              ".MuiButton-root": { flex: 1 },
            }}
          />
          <Typography level="body-xs">
            {tvMode === "tab"
              ? "Event info will display alongside normal TV content"
              : "TV will only display dedicated info"}
          </Typography>
        </Stack>
      </Stack>
    </>
  );
};

export default CompetitionDetailsForm;
